import styled from "styled-components";
import {brand} from "../../theme/tokens";

/**
 * Externalized styles for {@link ./ModeToggle} (restored in ui#215), per the owner's no-inline-styles
 * rule (CLAUDE.md, 2026-07-12) — the cards were reworked back into the panel, so their styles move out
 * of the component file. Selected cards use the composer's personalization accent, with the existing
 * product tokens as fallbacks. The default blue icon tile keeps its white glyph in both themes.
 */

/** The radiogroup rail: two evenly-flexed cards that fill the width of the panel row (ui#215). */
export const Method = styled.div`
	display: flex;
	width: 100%;
	gap: 8px;
	margin-top: 2px;
`;

/** A single mode radio-card — borderless (owner 2026-09-11): the fill alone marks selection,
 *  matching the borderless accent wash the file summary got in the same ticket. */
export const Card = styled.label<{$on: boolean}>`
	position: relative;
	flex: 1 1 0;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
	text-align: left;
	padding: 12.5px 13.5px;
	border: none;
	background: ${({theme, $on}) => ($on ? `var(--sender-accent-tint, ${theme.tokens.accentTint})` : theme.tokens.field)};
	border-radius: 13px;
	cursor: pointer;
	font-family: inherit;
	transition: 0.16s;

	&:hover {
		background: ${({theme, $on}) => ($on ? `var(--sender-accent-tint, ${theme.tokens.accentTint})` : theme.tokens.line)};
	}

	&:has(input:focus-visible) {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 1px;
	}
`;

/** The native radio owns focus, selection, arrow navigation, and the group's single tab stop. */
export const ModeRadioInput = styled.input`
	position: absolute;
	inset: 0;
	z-index: 1;
	width: 100%;
	height: 100%;
	margin: 0;
	opacity: 0;
	cursor: pointer;
`;

/** Card header row: icon tile, mode name, and the trailing check on the active card. */
export const Top = styled.span`
	display: flex;
	align-items: center;
	gap: 7px;
	width: 100%;
`;

export const IconTile = styled.span<{$on: boolean}>`
	width: 25px;
	height: 25px;
	border-radius: 7px;
	background: ${({theme, $on}) => ($on ? `var(--sender-accent, ${theme.tokens.blue})` : theme.tokens.card)};
	color: ${({theme, $on}) => ($on ? `var(--sender-ink-on-accent, ${brand.white})` : theme.tokens.ink2)};
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
`;

export const Name = styled.span`
	font-weight: 700;
	font-size: 14px;
	color: ${({theme}) => theme.tokens.ink};
`;

export const Check = styled.span<{$on: boolean}>`
	margin-left: auto;
	color: ${({theme}) => (theme.tokens.mode === "dark" ? theme.tokens.segInk : theme.tokens.blue)};
	display: ${({$on}) => ($on ? "flex" : "none")};
	align-items: center;
`;

/** The description line ("Files stream directly between devices…") the ui#214 tabs had dropped. */
export const Desc = styled.span`
	font-size: 11.5px;
	color: ${({theme}) => theme.tokens.ink2};
	line-height: 1.4;
`;
